// angular imports
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

// service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { UserService } from '../../../_services/data-services/user.service';

// cc-common imports
import { IconConstants, MessageBoxComponent } from 'cc-common';
import { WorkFlowParametersDto } from '../../_models/workflow-parameters-dto';
import { WorkflowSharedService } from '../../_services/workflow-shared.service';

@Component({
    selector: 'app-workflow-job-details',
    templateUrl: './workflow-job-details.component.html',
    styleUrls: ['./workflow-job-details.component.scss']
})
export class WorkflowJobDetailsComponent implements OnInit, OnChanges {
    @ViewChild('workflowMessageBox', { static: false }) workflowMessageBox: MessageBoxComponent;

    @Input() payee: string;
    @Input() payeeCode: string;
    @Input() payeeType: string;
    @Input() managementCompanyVendorId: number;
    @Input() hasVendorNotes = false;
    @Input() hasNotes = false;
    @Input() hasBoardNotes = false;
    @Input() communityId: number;
    @Input() workflowId: number;
    @Input() vendorAccountNumber: string;
    @Input() pdfSource: string;
    @Input() hasVendorWFCheckList = false;
    @Input() hasBoardApproval = false;
    @Input() boardApprovalEnabled: boolean;
    @Input() hasWorkOrders = false;
    @Input() hasContracts = false;
    @Input() vendorInstruction: string;
    @Input() closeAllChildPopup = false;

    @Output() onForceRefresh = new EventEmitter<void>();
    @Output() onVendorChecklistShow = new EventEmitter<void>();
    @Output() onWorkOrderClick = new EventEmitter<void>();
    @Output() onContractsClick = new EventEmitter<void>();

    showInternalNotes = false;
    showBoardNotes = false;
    showHistory = false;
    showVendorNotesPopUp = false;
    showVendorInstructions = false;
    showBoardApproval = false;

    pdfWindow: any;

    iconNotes = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes = IconConstants.NOTES_EMPTY;
    iconPDF = IconConstants.PDF_FILE;
    iconHistory = IconConstants.HISTORY;
    iconClose = IconConstants.CLOSE;
    iconCross = IconConstants.CROSS;
    iconCheck = IconConstants.CHECK;
    iconChecklist = IconConstants.TASK;
    iconInstructions = IconConstants.INFO;
    iconHardHat = IconConstants.HARD_HAT;
    iconFileContract = IconConstants.FILE_CONTRACT;

    isInternalUser: boolean;

    constructor(
        private readonly helperService: HelperService,
        private readonly userService: UserService,
        private readonly workflowSharedService: WorkflowSharedService
    ) {}

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasNotes && changes.hasNotes.currentValue) {
            this.hasNotes = changes.hasNotes.currentValue;
        }
        if (changes.hasBoardNotes && changes.hasBoardNotes.currentValue) {
            this.hasBoardNotes = changes.hasBoardNotes.currentValue;
        }
        if (changes.hasVendorNotes && changes.hasVendorNotes.currentValue) {
            this.hasVendorNotes = changes.hasVendorNotes.currentValue;
        }
        if (changes.payee && changes.payee.currentValue) {
            this.payee = changes.payee.currentValue;
        }

        if (changes.closeAllChildPopup && changes.closeAllChildPopup.currentValue) {
            this.closeAllChildPopup = changes.closeAllChildPopup.currentValue;
            if (this.closeAllChildPopup) {
                this.closeAllPopups();
            }
        }
    }

    onInternalNotesClick(): void {
        this.showInternalNotes = true;
    }

    onInternalNotesPopClose(refresh: boolean): void {
        this.showInternalNotes = false;
        if (!this.hasNotes) {
            this.hasNotes = refresh;
        }
        this.forceRefresh(refresh);
    }

    onBoardNotesClick(): void {
        this.showBoardNotes = true;
    }

    onBoardNotesClose(refresh: boolean): void {
        this.showBoardNotes = false;
        if (!this.hasBoardNotes) {
            this.hasBoardNotes = refresh;
        }
        this.forceRefresh(refresh);
    }

    onWorkflowHistory(): void {
        this.showHistory = true;
    }

    onHistoryClose(): void {
        this.showHistory = false;
    }

    onPdfClick(): void {
        const baseParams: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                if (!!result) {
                    const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                    if (this.pdfWindow && !this.pdfWindow.closed) {
                        this.pdfWindow.close();
                    }
                    this.pdfWindow = window.open(
                        fileURL,
                        '_blank',
                        'location=yes,height=670,width=620,scrollbars=yes,status=yes'
                    );
                }
            },
            (error) => {
                if (error.status === 404) {
                    this.workflowMessageBox.showErrorMessage(
                        'Error',
                        'Something went wrong, We are unable to find the invoice. Please try again later.'
                    );
                } else {
                    this.workflowMessageBox.showErrorMessage('Error', error.error);
                }
            }
        );
    }

    onVendorActivitiesClick(): void {
        this.helperService.openSearchInvoice(this.communityId, this.managementCompanyVendorId);
    }

    onVendorNotesClick(): void {
        this.showVendorNotesPopUp = true;
    }

    onVendorNotesClose(refresh: boolean): void {
        this.showVendorNotesPopUp = false;
        if (!this.hasVendorNotes) {
            this.hasVendorNotes = refresh;
        }
        this.forceRefresh(refresh);
    }

    onVendorInstructionsClick(): void {
        this.showVendorInstructions = true;
    }

    onVendorInstructionPopUpClose(): void {
        this.showVendorInstructions = false;
    }

    onVendorChecklistClick(): void {
        this.onVendorChecklistShow.emit();
    }

    onBoardApprovalClick(): void {
        this.showBoardApproval = true;
    }

    onBoardApprovalClose(): void {
        this.showBoardApproval = false;
    }

    onWorkOrdersClick(): void {
        this.onWorkOrderClick.emit();
    }

    onContractClick(): void {
        this.onContractsClick.emit();
    }

    onVendorClick(): void {
        const contextType = 'Community';
        const queryString = `contextType=${contextType}&contextId=${this.communityId}&vendorCode=${encodeURIComponent(
            this.payeeCode
        )}`;

        this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
    }

    private forceRefresh(flag: boolean): void {
        if (flag) {
            this.onForceRefresh.emit();
        }
    }

    private closeAllPopups(): void {
        this.showBoardNotes = false;
        this.showHistory = false;
        this.showInternalNotes = false;
        this.showVendorInstructions = false;
        this.showVendorNotesPopUp = false;
    }
}
